/**
 * NPC 配置块 JSON Schema 定义
 */
export const npcSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/npc.json',
  title: 'NPC 配置',
  description: 'NPC（非玩家角色）配置数据结构',
  type: 'object',
  required: ['npc_id', 'display_name'],
  properties: {
    npc_id: {
      type: 'string',
      title: 'NPC ID',
      description: 'NPC 唯一标识符',
      pattern: '^npc_[a-zA-Z0-9_]+$',
      minLength: 5,
      maxLength: 50,
      examples: ['npc_mysterious_man', 'npc_old_merchant'],
    },
    display_name: {
      type: 'string',
      title: '显示名称',
      description: 'NPC 在游戏中显示的名称',
      minLength: 1,
      maxLength: 50,
      examples: ['神秘男人', '老商人', '守卫队长'],
    },
    role: {
      type: 'string',
      title: '角色定位',
      description: 'NPC 在游戏中的角色定位',
      enum: ['主要角色', '次要角色', '背景角色', '商人', '守卫', '向导', '敌对'],
      default: '次要角色',
    },
    personality: {
      type: 'array',
      title: '性格特征',
      description: 'NPC 的性格特征标签',
      items: {
        type: 'string',
        minLength: 1,
        maxLength: 20,
      },
      minItems: 0,
      maxItems: 10,
      uniqueItems: true,
      examples: [['冷静', '谨慎', '神秘'], ['热情', '健谈', '乐观']],
    },
    background: {
      type: 'string',
      title: '背景故事',
      description: 'NPC 的背景故事',
      minLength: 0,
      maxLength: 2000,
    },
    dialogue_style: {
      type: 'string',
      title: '对话风格',
      description: 'NPC 的对话风格描述',
      minLength: 0,
      maxLength: 500,
      examples: ['简短、神秘', '热情、健谈', '严肃、正式'],
    },
    system_prompt: {
      type: 'string',
      title: 'AI 系统提示词',
      description: 'AI 对话系统的提示词',
      minLength: 0,
      maxLength: 5000,
    },
    ai_model: {
      type: 'string',
      title: 'AI 模型',
      description: '使用的 AI 模型标识',
      enum: [
        'doubao-seed-evolving',
        'doubao-seed-2-1-pro-260628',
        'doubao-seed-2-1-turbo-260628',
        'doubao-pro-32k',
        'doubao-pro-4k',
        'gpt-4',
        'gpt-3.5-turbo',
      ],
      default: 'doubao-seed-evolving',
    },
    appearance: {
      type: 'object',
      title: '外观描述',
      description: 'NPC 的外观描述',
      properties: {
        age: {
          type: 'string',
          title: '年龄段',
          enum: ['青年', '中年', '老年', '未知'],
        },
        gender: {
          type: 'string',
          title: '性别',
          enum: ['男', '女', '未知'],
        },
        height: {
          type: 'string',
          title: '身高',
          enum: ['矮小', '普通', '高大'],
        },
        clothing: {
          type: 'string',
          title: '服装描述',
          maxLength: 200,
        },
        features: {
          type: 'array',
          title: '特征',
          items: {
            type: 'string',
            maxLength: 50,
          },
          maxItems: 5,
        },
      },
    },
    initial_attitude: {
      type: 'string',
      title: '初始态度',
      description: 'NPC 对玩家的初始态度',
      enum: ['友好', '中立', '敌对', '警惕', '热情'],
      default: '中立',
    },
    location: {
      type: 'string',
      title: '初始位置',
      description: 'NPC 的初始位置',
      maxLength: 100,
    },
    available_dialogues: {
      type: 'array',
      title: '可用对话',
      description: 'NPC 可用的对话列表',
      items: {
        type: 'object',
        required: ['dialogue_id', 'trigger_condition'],
        properties: {
          dialogue_id: {
            type: 'string',
            title: '对话 ID',
            pattern: '^dialogue_[a-zA-Z0-9_]+$',
          },
          trigger_condition: {
            type: 'string',
            title: '触发条件',
            description: '对话触发的条件表达式',
          },
          priority: {
            type: 'integer',
            title: '优先级',
            minimum: 0,
            maximum: 100,
            default: 50,
          },
        },
      },
    },
    relationships: {
      type: 'object',
      title: '关系网',
      description: '与其他 NPC 的关系',
      additionalProperties: {
        type: 'object',
        properties: {
          npc_id: {
            type: 'string',
            pattern: '^npc_[a-zA-Z0-9_]+$',
          },
          relationship_type: {
            type: 'string',
            enum: ['朋友', '敌人', '家人', '同事', '陌生人'],
          },
          intimacy: {
            type: 'integer',
            minimum: -100,
            maximum: 100,
            default: 0,
          },
        },
      },
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type NpcConfig = {
  npc_id: string
  display_name: string
  role?: string
  personality?: string[]
  background?: string
  dialogue_style?: string
  system_prompt?: string
  ai_model?: string
  appearance?: {
    age?: string
    gender?: string
    height?: string
    clothing?: string
    features?: string[]
  }
  initial_attitude?: string
  location?: string
  available_dialogues?: Array<{
    dialogue_id: string
    trigger_condition: string
    priority?: number
  }>
  relationships?: Record<
    string,
    {
      npc_id: string
      relationship_type: string
      intimacy?: number
    }
  >
  metadata?: Record<string, any>
}
